/// DebugControls: Toolbar-integrated debug control buttons.
/// Provides quick-access debug actions (toggle breakpoint, step, continue, stop)
/// with a mode indicator dot.
///
/// Phase 20, Plan 03.

import { Circle, SkipForward, Play, Square } from "lucide-react";
import { IconButton } from "@/components/common/IconButton";
import { useDebugSession } from "@/hooks/useDebugSession";
import { useDebugStore } from "@/stores/debugStore";
import { useEditorStore } from "@/stores/editorStore";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DebugControls() {
  const { step, continueExecution, stopDebug, toggleBreakpoint, mode } = useDebugSession();
  const pauseReason = useDebugStore((s) => s.pauseReason);

  // Only show when debug session is active OR a BPMN tab is active
  const activeTab = useEditorStore((s) => {
    const id = s.activeTabId;
    return id ? s.tabs.find((t) => t.id === id) : undefined;
  });

  const isBPMN = activeTab?.editorType === "bpmn";
  const isIdle = mode === "idle";
  const isPaused = mode === "paused";
  const isActive = !isIdle;

  // Only visible when debugging or when a BPMN tab is open
  if (!isActive && !isBPMN) return null;

  // For toggle breakpoint, get selected element from editorStore
  const handleToggleBreakpoint = () => {
    const editorState = useEditorStore.getState();
    const tabId = editorState.activeTabId;
    if (!tabId) return;
    const state = editorState.editorStates.get(tabId);
    if (!state?.selection.selectedIds.length) return;
    const elementId = state.selection.selectedIds[0];
    // Use the element's BPMN ID from properties, or fall back to elementId
    const bpmnId = (state.selection.elementProperties?.id as string) ?? elementId;
    toggleBreakpoint(elementId, bpmnId);
  };

  return (
    <div className="flex items-center gap-0.5">
      {/* Mode indicator */}
      <div className="flex items-center mr-1" title={`Debug: ${mode}`}>
        <span className={`inline-block h-2 w-2 rounded-full ${getModeColor(mode)}`} />
      </div>

      {/* Toggle Breakpoint */}
      <IconButton
        icon={<Circle className="h-3.5 w-3.5 text-red-500 fill-red-500" />}
        label="Toggle Breakpoint"
        onClick={handleToggleBreakpoint}
        size="sm"
      />

      {/* Step */}
      <IconButton
        icon={<SkipForward className="h-3.5 w-3.5" />}
        label="Step Over (F10)"
        disabled={!isPaused || pauseReason === "stopped"}
        onClick={step}
        size="sm"
      />

      {/* Continue */}
      <IconButton
        icon={<Play className="h-3.5 w-3.5" />}
        label="Continue (F5)"
        disabled={!isPaused}
        onClick={continueExecution}
        size="sm"
      />

      {/* Stop Debug */}
      <IconButton
        icon={<Square className="h-3.5 w-3.5" />}
        label="Stop Debug (Shift+F5)"
        disabled={isIdle}
        onClick={stopDebug}
        size="sm"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getModeColor(mode: string): string {
  switch (mode) {
    case "running":
      return "bg-green-500";
    case "paused":
      return "bg-yellow-500";
    case "stepping":
      return "bg-red-500";
    default:
      return "bg-zinc-500";
  }
}
